import { Text } from "@instacover/ui"
import { createFileRoute } from "@tanstack/react-router"
import { useNavigate } from "@tanstack/react-router"
import { lazy, Suspense } from "react"
import { Trans } from "react-i18next"

import ArrowButton from "@/components/base/ArrowButton"
import { db } from "@/database/database"
import { useGetAddress } from "@/hooks/api/useGetAddress"
import { useSetAddressCorrect } from "@/hooks/api/useSetAddressCorrect"
import { BaseLayout } from "@/sections/layouts/BaseLayout"
import { logger } from "@/utils/logging"

export const Route = createFileRoute("/$sessionId/_session/_steps/address")({
  component: AddressRoute,
})

const Map = lazy(() => import("@/components/Map"))

function AddressRoute() {
  const { sessionId } = Route.useParams()
  const navigate = useNavigate()

  const { data: instahomeAddress } = useGetAddress(sessionId)
  const { mutate: setAddressCorrect } = useSetAddressCorrect()

  function onSetAddressCorrect(isCorrect: boolean) {
    logger.debug("Setting address correctness to", isCorrect)
    setAddressCorrect(
      { sessionId, isCorrect },
      {
        onSuccess: async () => {
          const nextStep = await db.orderedStep.finishAndGetNext({ sessionId, step: "Address" })
          logger.debug("Address step marked as finished in local database")

          if (nextStep) {
            logger.debug("Navigating to next step:", nextStep)
            navigate({ to: nextStep.to, params: { ...nextStep.params } })
          }
        },
      },
    )
  }

  return (
    <BaseLayout
      aboveHeading={
        <div className="bg-ic-background-muted w-full max-w-[1400px]">
          <Suspense>
            {instahomeAddress?.expectedGeoLocation && (
              <Map
                sessionId={sessionId}
                geoJsons={[
                  {
                    type: "ExpectedGeoLocation",
                    geoJson: instahomeAddress.expectedGeoLocation as unknown as GeoJSON.FeatureCollection,
                  },
                ]}
                zoomControl={true}
              />
            )}
          </Suspense>
        </div>
      }
      heading={{
        i18nKey: "address.heading",
      }}
      descriptionNode={
        <div>
          <Text className="text-ic-primary font-bold">
            <Trans i18nKey={"address.expectedAddress"} />
          </Text>

          <Text as="span">{instahomeAddress?.expectedFullAddress}</Text>
        </div>
      }
      buttons={
        <div className="flex justify-end">
          <ArrowButton variant="primary" onClick={() => onSetAddressCorrect(true)} data-testid="address-continue-btn" />
        </div>
      }
    />
  )
}
